import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FuseLoading from '@fuse/core/FuseLoading';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import SimpleDialog from '@fuse/core/SimpleDialog';
import handleError from '@fuse/core/errorHandler';
import { useDeleteTagMutation, useGetAllTagsQuery } from 'src/app/services/claim/claimApi';
import ConfirmDeleteModalWindow from 'app/shared-components/confirmDeleteModalWindow';
import TagsForm from './TagsForm';

const Tags = () => {
  const { t } = useTranslation('translations');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isOpenDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [currentTag, setCurrentTag] = useState(null);

  const { data: tags, refetch, isLoading } = useGetAllTagsQuery({ all: true });
  const [deleteTag] = useDeleteTagMutation();

  const typeLabels = {
    0: t('Text'),
    1: t('Number'),
    2: t('Date'),
    3: t('Employee'),
  };

  const handleEdit = (tag) => {
    setCurrentTag(tag);
    setIsDialogOpen(true);
  };

  const handleSelectedItemForDelete = (item) => {
    setSelectedItem(item);
    setOpenDeleteModal(true);
  };

  const handleDeleteTag = async ({ id }) => {
    try {
      await deleteTag({ id })
        .unwrap()
        .then(() => {
          setOpenDeleteModal(false);
          refetch();
        });
    } catch (err) {
      handleError(err);
    }
  };

  const addNewTag = () => {
    setIsDialogOpen(true);
    setCurrentTag(null);
  };

  const getTypeLabel = (type) => typeLabels[type] || '-';

  if (isLoading) return <FuseLoading />;

  return (
    <div style={{ display: 'grid' }}>
      <TableContainer>
        <div className="h-[40px] flex flex-row justify-between items-center dark:bg-dark">
          <h2 className="ml-5">{t('Tags')}</h2>
          <Button
            className="mx-8"
            size="small"
            variant="outlined"
            color="secondary"
            onClick={addNewTag}
          >
            <FuseSvgIcon size={20}>react-outline:plus</FuseSvgIcon>
            <span className="mx-8">{t('Add')}</span>
          </Button>
        </div>
        <Table style={{ overflow: 'auto' }}>
          <TableHead>
            <TableRow className="dark:bg-dark">
              <TableCell>{t('Title')}</TableCell>
              <TableCell>{t('Type')}</TableCell>
              <TableCell>{t('Valuess')}</TableCell>
              <TableCell>{t('Actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody className="h-[1000px] overflow-y-auto">
            {tags?.map((item) => (
              <TableRow key={item.id} className="bg-white dark:bg-dark hover:bg-grey-200">
                <TableCell>{item.name}</TableCell>
                <TableCell>{getTypeLabel(item.type)}</TableCell>
                <TableCell>{item.fio ? item.fio : item.value}</TableCell>
                <TableCell component="td" scope="row">
                  <Tooltip className="cursor-pointer" placement="top" arrow title={t('Edit')}>
                    <EditIcon
                      onClick={() => handleEdit(item)}
                      edge="end"
                      aria-label="edit"
                      className="mr-5"
                      color="info"
                    />
                  </Tooltip>
                  <Tooltip className="cursor-pointer" title={t('Delete')} placement="top" arrow>
                    <DeleteIcon
                      onClick={() => handleSelectedItemForDelete(item)}
                      edge="end"
                      aria-label="edit"
                      className="mr-5"
                      color="error"
                    />
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <SimpleDialog
        title={currentTag ? t('Editing') : t('Adding')}
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
      >
        <TagsForm refetch={refetch} setOpen={setIsDialogOpen} tag={currentTag} />
      </SimpleDialog>

      {isOpenDeleteModal && (
        <ConfirmDeleteModalWindow
          onClose={() => setOpenDeleteModal(false)}
          open={isOpenDeleteModal}
          setIsOpenModal={setOpenDeleteModal}
          modalTitle={t('AreYouSureToDeleteTag')}
          deleteMessage={() => handleDeleteTag(selectedItem)}
        />
      )}
    </div>
  );
};

export default Tags;
